<template>
  <d-modal
    @close="handleClose"
    size="lg">
    <d-modal-header>
      <d-modal-title>
        {{ $t('product.addVariantPopup.title') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-alert
        v-if="loadError"
        theme="warning"
        show
        class="text-center">
        {{ $t('ui.loadError') }}
      </d-alert>
      <d-alert
        v-if="!loadedProducts && !loadError"
        theme="light"
        show
        class="text-center">
        {{ $t('ui.dataLoading') }}
      </d-alert>
      <d-form v-show="loadedProducts && !loadError">
        <validation-observer ref="product-variant-add-form" tag="form">
          <div class="form-group">
            <label>
              {{ $t('product.productName') }}:
            </label>

            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('product.productCode') }}:
            </label>

            <validation-provider
              rules="required"
              name="code"
              v-slot="{ errors }">
              <d-input-group
                :prepend="parentProductCode"
                :class="{
                  'is-invalid': errors.length
                }">
                <d-input
                  :state="errors.length ? 'invalid' : null"
                  v-model="code" />
              </d-input-group>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('product.eanGtinCode') }}:
            </label>

            <d-input
              v-model="ean" />
          </div>

          <div class="form-group">
            <label class="d-block">
              {{ $t('product.addVariantPopup.importPriceMethod') }}:
            </label>

            <d-button-group size="small">
              <d-button
                theme="primary"
                :outline="import_price_method !== 'PARENT'"
                @click.prevent="setImportPriceMethod('PARENT')">
                {{ $t('product.addVariantPopup.importPriceMethods.parent') }}
              </d-button>
              <d-button
                theme="primary"
                :outline="import_price_method !== 'OTHER_PRODUCT'"
                @click.prevent="setImportPriceMethod('OTHER_PRODUCT')">
                {{ $t('product.addVariantPopup.importPriceMethods.other_product') }}
              </d-button>
              <d-button
                theme="primary"
                :outline="import_price_method !== 'OTHER_CHILD'"
                @click.prevent="setImportPriceMethod('OTHER_CHILD')">
                {{ $t('product.addVariantPopup.importPriceMethods.other_child') }}
              </d-button>
              <d-button
                theme="primary"
                :outline="import_price_method !== 'CUSTOM'"
                @click.prevent="setImportPriceMethod('CUSTOM')">
                {{ $t('product.addVariantPopup.importPriceMethods.custom') }}
              </d-button>
            </d-button-group>
          </div>

          <div
            v-if="import_price_method === 'OTHER_CHILD'"
            class="form-group">
            <label class="d-block">
              {{ $t('product.addVariantPopup.selectVariant') }}:
            </label>
            <validation-provider
              rules="required"
              name="other_child"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  :options="parentProductVariants"
                  v-model="other_child"
                  :reduce="product => product.id"
                  :filterBy="filterByName"
                  :placeholder="$t('product.addVariantPopup.selectProductVariant')"
                  label="name">
                  <template slot="no-options">
                    <template>{{ $t('product.addVariantPopup.noVariantsMatchingPhraseFound') }}&hellip;</template>
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      {{ option.name }}
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div
            v-if="import_price_method === 'OTHER_PRODUCT'"
            class="form-group">
            <label class="d-block">
              {{ $t('product.addVariantPopup.selectProduct') }}:
            </label>
            <validation-provider
              rules="required"
              name="other_product"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  :options="products"
                  v-model="other_product"
                  :reduce="product => product.id"
                  :filterBy="filterByName"
                  :placeholder="$t('product.addVariantPopup.selectProduct')"
                  label="name">
                  <template slot="no-options">
                    <template>{{ $t('product.addVariantPopup.noProductsMatchingPhraseFound') }}&hellip;</template>
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      {{ option.name }}
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label class="d-block">
              {{ $t('product.addVariantPopup.priceCode') }}:
            </label>

            <span
              v-if="import_price_method !== 'CUSTOM'"
              class="text-primary">
              {{ price_code }}
            </span>

            <template v-if="import_price_method === 'CUSTOM'">
              <validation-provider
                rules="required"
                name="price_code"
                v-slot="{ errors }">
                <d-input-group
                  :prepend="parentProductPriceCode + '-'"
                  :class="{
                    'is-invalid': errors.length
                  }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="price_code" />
                </d-input-group>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </template>
          </div>

          <table class="table table-sm">
            <thead>
              <tr>
                <th>{{ $t('product.addVariantPopup.fieldName') }}</th>
                <th style="width: 140px;" class="text-right">{{ $t('product.shouldBeCopied') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ $t('product.automaticallySetSamplesDates') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['auto_sample_date']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.includeDiscounts') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['include_discounts']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.includeInServices') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['include_in_services']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.disableForCustomer') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['disable_for_customer']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.disableForCustomerAPI') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['disable_for_customer_api']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.availableInPurchases') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['available_in_purchase']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.indentProduct.weight') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['weight']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.indentProduct.cnCode') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['cn_code']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.linkToWWWPage') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['site_url']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.linkToDropbox') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['dropbox_url']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.productPhoto') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['image']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.visualizationTemplate') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['visualization_template']" /></td>
              </tr>
              <tr>
                <td>{{ $t('ui.shortDescription') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['intro']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.productDescription') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['description']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.siteDescription') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['site_description']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.descriptionForCustomer') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['description_for_customer']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.minimalQty') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['minimum_quantity']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.piecesInBox') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['pieces_in_carton']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.actualPiecesInBox') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['real_pieces_in_carton']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.productPrices') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['prices']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.importantPriceInfo') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['price_message']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.productImagesGallery') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['images']" /></td>
              </tr>
              <tr>
                <td>{{ $t('image.webGallery') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['web_images']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.productTranslations') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['translations']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.productTags') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['tags']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.productDefaultServices') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['default_services']" /></td>
              </tr>
              <tr>
                <td>{{ $t('ui.supplier') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['purchase_customer_id']" /></td>
              </tr>
              <tr>
                <td>{{ $t('ui.subSupplier') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['purchase_sub_customer_id']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.properties.color') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['color_id']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.properties.strand') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['strand_id']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.properties.sizes') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['sizes']" /></td>
              </tr>
              <tr>
                <td>{{ $t('product.properties.procedures') }}</td>
                <td class="d-flex"><d-checkbox toggle toggle-small class="mb-0 ml-auto" v-model="valuesOfProductToCopy['procedures']" /></td>
              </tr>
            </tbody>
          </table>
        </validation-observer>
      </d-form>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel"
        :disabled="saveInProgress">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm"
        :disabled="saveInProgress">
        <div
          v-if="saveInProgress"
          class="spinner-border spinner-border-sm"
          role="status"></div>
        <i
          v-else
          class="material-icons">
          add
        </i>
        {{ $t('product.addVariantPopup.addVariant') }}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'product-variant-new-popup',
  props: {
    parentProductID: {
      default: 0,
      type: Number
    },
    parentProductName: {
      default: '',
      type: String
    },
    parentProductCode: {
      default: '',
      type: String
    },
    parentProductPriceCode: {
      default: '',
      type: String
    },
    parentProductVariants: {
      default: () => ([]),
      type: Array
    }
  },
  data () {
    return {
      name: this.parentProductName,
      code: '',
      ean: '',
      import_price_method: 'PARENT',
      price_code: this.parentProductPriceCode,
      valuesOfProductToCopy: {
        auto_sample_date: true,
        include_discounts: true,
        include_in_services: true,
        disable_for_customer: true,
        disable_for_customer_api: true,
        available_in_purchase: true,
        weight: true,
        cn_code: true,
        site_url: true,
        dropbox_url: true,
        image: true,
        visualization_template: true,
        intro: true,
        description: true,
        site_description: true,
        description_for_customer: true,
        minimum_quantity: true,
        pieces_in_carton: true,
        real_pieces_in_carton: true,
        prices: true,
        price_message: true,
        images: true,
        web_images: true,
        translations: true,
        tags: true,
        default_services: true,
        purchase_customer_id: true,
        purchase_sub_customer_id: true,
        color_id: true,
        strand_id: true,
        sizes: true,
        procedures: true
      },
      // helpers
      other_child: null,
      other_product: null,
      products: [],
      loadedProducts: false,
      loadError: false,
      saveInProgress: false
    };
  },
  watch: {
    'other_child': function (newValue) {
      if (!newValue) {
        this.price_code = '';
      } else {
        let foundedChild = this.parentProductVariants.filter(product => product.id === newValue);

        if (foundedChild[0]) {
          this.price_code = foundedChild[0].price_code;
        } else {
          this.price_code = '';
        }
      }
    },
    'other_product': function (newValue) {
      if (!newValue) {
        this.price_code = '';
      } else {
        let foundedProduct = this.products.filter(product => product.id === newValue);

        if (foundedProduct[0]) {
          this.price_code = foundedProduct[0].price_code;
        } else {
          this.price_code = '';
        }
      }
    }
  },
  mounted () {
    this.loadProducts();
  },
  methods: {
    handleClose () {
      this.$bus.$emit('product-variant-new-popup-close');
    },
    validateForm () {
      FormUtils.validate(this.$refs['product-variant-add-form'], this.saveProductVariant);
    },
    saveProductVariant () {
      this.saveInProgress = true;
      let fields = this.prepareFields();
      let priceCode = this.price_code;

      if (this.import_price_method === 'CUSTOM') {
        priceCode = this.parentProductPriceCode + '-' + priceCode;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/products/store',
        formData: {
          parent_id: this.parentProductID,
          is_indent: 0,
          name: this.name,
          code: this.parentProductCode + this.code,
          ean: this.ean,
          import_price_method: this.import_price_method,
          price_code: priceCode,
          fields
        },
        successTitle: this.$t('product.addVariantPopup.addVariantSuccessTitle'),
        successText: this.$t('product.addVariantPopup.addVariantSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('product.addVariantPopup.addVariantErrorText'),
        successAction: () => {
          this.saveInProgress = false;
          this.$bus.$emit('product-edit-variants-reload-needed');
          this.handleClose();
        },
        failAction: () => {
          this.saveInProgress = false;
        }
      });
    },
    setImportPriceMethod (methodName) {
      if (methodName === 'PARENT') {
        this.import_price_method = 'PARENT';
        this.price_code = this.parentProductPriceCode;
        this.other_child = null;
        this.other_product = null;
      } else if (methodName === 'OTHER_CHILD') {
        this.import_price_method = 'OTHER_CHILD';
        this.price_code = '';
        this.other_child = null;
        this.other_product = null;
      } else if (methodName === 'OTHER_PRODUCT') {
        this.import_price_method = 'OTHER_PRODUCT';
        this.price_code = '';
        this.other_child = null;
        this.other_product = null;
      } else if (methodName === 'CUSTOM') {
        this.import_price_method = 'CUSTOM';
        this.price_code = '';
        this.other_child = null;
        this.other_product = null;
      }
    },
    prepareFields () {
      let output = [];
      let fieldsToCopy = Object.keys(this.valuesOfProductToCopy);

      for (let i = 0; i < fieldsToCopy.length; i++) {
        let fieldName = fieldsToCopy[i];

        if (this.valuesOfProductToCopy[fieldName]) {
          output.push(fieldName);
        }
      }

      return output;
    },
    cancel () {
      this.handleClose();
    },
    loadProducts () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError',
        params: {
          where: {
            is_indent: 0
          }
        }
      });
    },
    filterByName (option, label, search) {
      return (option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    }
  }
}
</script>
